import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  CARD_TYPES,
  WIZARD_CARDS_ELEVATION,
} from '../../../shared/constants';
import { IContentfulLink, IImage, IMdBody } from '../../../types/shared';

import NumberIcon from '../../atoms/icons/NumberIcon';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, useWindowDimensions } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { replaceUtmParam } from '../../../utils/string-utils/string-utils';

import mq from '../../../styles/breakpoints';
import buttonStyles from '../../../styles/buttonStyles';
import Colors from '../../../styles/colors';
import { Body, H4 } from '../../../styles/generic';

interface Props {
  title: string;
  body: IMdBody;
  image: IImage;
  primaryLink: IContentfulLink;
  cardNumber: number;
  cardElevation?: 'low' | 'high';
  extraLinkParams?: string;
}

const WizardResultCard = ({
  title,
  body,
  image,
  primaryLink,
  cardNumber,
  cardElevation,
  extraLinkParams,
}: Props) => {
  const { width } = useWindowDimensions();

  const renderIcon = () => (
    <div>
      {image?.gatsbyImageData ? (
        <ImageWrapper data-testid="card-gatsby-image">
          <StyledGatsbyImage alt="" image={image.gatsbyImageData} />
        </ImageWrapper>
      ) : (
        image?.file && (
          <ImageWrapper data-testid="card-file-image">
            <Image alt="" src={image.file.url} />
          </ImageWrapper>
        )
      )}
    </div>
  );

  const primaryCta = primaryLink ? getCtaLink(primaryLink) : undefined;
  const primaryCtaOptionalParams =
    extraLinkParams && primaryLink
      ? replaceUtmParam(primaryCta as string, extraLinkParams)
      : primaryCta;

  return (
    <Container data-testid="wizard-result-card" cardElevation={cardElevation}>
      {!image && <NumberIcon number={cardNumber} />}
      <ContentWrapper hasImage={!!image}>
        <TextWrapper>
          {title && (
            <HeaderContainer>
              <StyledTitle data-testid="card-title">{title}</StyledTitle>{' '}
              {image && width < 768 && renderIcon()}
            </HeaderContainer>
          )}
          {body && (
            <Body data-testid="card-body">
              <ReactMarkdownExtended componentType={CARD_TYPES.wizardCard}>
                {body.childMarkdownRemark?.rawMarkdownBody}
              </ReactMarkdownExtended>
            </Body>
          )}
        </TextWrapper>
        {primaryLink && (
          <LinkWrapper>
            <StyledSecondaryButton
              type={primaryLink?.type}
              label={primaryLink.label || primaryLink.name}
              buttonTheme={primaryLink.theme || BUTTON_THEMES.default}
              to={primaryCtaOptionalParams}
              iconPosition={primaryLink?.iconPosition}
              icon={primaryLink?.icon}
              deqComponentTitle={primaryLink.label}
              deqCustomAttributes={{
                type: DEQ_TYPES.textLink,
              }}
              target="_blank"
            />
          </LinkWrapper>
        )}
      </ContentWrapper>
      {image && width >= 768 && renderIcon()}
    </Container>
  );
};

export default WizardResultCard;

const LinkWrapper = styled.div`
  margin-top: 8px;

  ${mq.from.breakpoint.M`
      margin-top: 16px;
  `}
`;

const Container = styled.div<{ cardElevation?: string }>`
  background-color: ${Colors.white};
  box-shadow: ${({ cardElevation, theme }) =>
    cardElevation === WIZARD_CARDS_ELEVATION.low
      ? theme.elevations.abnElevationWell
      : theme.elevations.abnPopover};
  display: flex;
  gap: 8px;
  margin-bottom: 1px;
  padding: 16px;

  ${mq.from.breakpoint.M`
    gap: 24px;
    align-items: center;
  `}

  ${mq.from.breakpoint.L`
    padding: 24px;
  `}
`;

const TextWrapper = styled.div`
  flex: 1;
`;

const ContentWrapper = styled.div<{ hasImage?: boolean }>`
  flex-direction: column;
  position: relative;

  ${({ hasImage }) =>
    hasImage
      ? css`
          flex-grow: 1;
        `
      : css`
          flex: 1;
        `}
`;

const StyledTitle = styled(H4)`
  flex-grow: 1;
  margin: 0 4px 0 0;

  ${mq.from.breakpoint.M`
    max-width: unset;
    margin: 0 0 8px;
  `}
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  width: inherit;
`;

const Image = styled.img`
  width: inherit;
`;

const ImageWrapper = styled.div`
  display: block;
  height: 36px;
  width: 36px;

  ${mq.from.breakpoint.M`
    height: 72px;
    width: 72px;
  `}
`;

const HeaderContainer = styled.div`
  ${mq.to.breakpoint.M`
    align-items: center;
    display: inline-flex;
    flex-direction: row;
    gap: 4px; 
    margin: 8px 0;
    width: 100%;
  `}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;
